import * as Y from 'yjs';
import { Observable } from 'lib0/observable';
import { createClient } from '@supabase/supabase-js';

const BATCH_DB    = 1000;    // DB write batch: 1s
const POLL_FAST   = 6000;    // 6s when broadcast is down (primary sync)
const POLL_SLOW   = 60000;   // 60s when broadcast is up (safety net only)

export class SupabaseProvider extends Observable {
  constructor(doc, { apiBase = '/api', getToken, userId }) {
    super();
    this.doc = doc;
    this.apiBase = apiBase;
    this.getToken = getToken;
    this.userId = userId;
    this.connected = false;
    this.synced = false;
    this.lastId = 0;
    this.pendingUpdates = [];
    this.batchTimer = null;
    this.pollTimer = null;
    this.supabase = null;
    this.channel = null;
    this.broadcastReady = false;
    this.destroyed = false;
    this._broadcastReconnectAttempt = 0;
    this._broadcastReconnectTimer = null;
    this._lastSnapshotFingerprint = 0;
    this._snap3minTimer = null;
    this._snapHourlyTimer = null;
    this._snapDailyTimer = null;

    this._onDocUpdate = this._onDocUpdate.bind(this);
    this._onVisibility = this._onVisibility.bind(this);
    this._onPageShow = this._onPageShow.bind(this);
    this._onFocus = this._onFocus.bind(this);
    this._lastResumeAt = 0;
  }

  // ── Connect ──

  async connect() {
    if (this.destroyed) return;
    this.doc.off('update', this._onDocUpdate);
    this.doc.on('update', this._onDocUpdate);
    this.emit('status', [{ status: 'syncing' }]);

    try {
      const token = this.getToken();
      if (!token) return;

      // 1. Fetch full state from server (first page)
      const res = await fetch(`${this.apiBase}/yjs/state`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`State fetch failed: ${res.status}`);
      const data = await res.json();

      // 2. Capture pre-sync local node count for conflict detection (Step 6 安全策).
      const preNodeCount = this._safeNodeCount(this.doc);
      const preState = preNodeCount > 0 ? Y.encodeStateAsUpdate(this.doc) : null;

      // 3. Merge server state into local doc
      const serverDoc = new Y.Doc();
      if (data.state) {
        const bytes = b64(data.state);
        Y.applyUpdate(this.doc, bytes, 'remote');
        Y.applyUpdate(serverDoc, bytes);
      }
      for (const u of data.updates) {
        const bytes = b64(u.update);
        Y.applyUpdate(this.doc, bytes, 'remote');
        Y.applyUpdate(serverDoc, bytes);
        this.lastId = Math.max(this.lastId, u.id);
      }

      // 2b. Drain remaining paginated updates
      let hasMore = !!data.hasMore;
      let safety = 50;  // max 50 pages (10000 updates) as runaway guard
      while (hasMore && safety-- > 0) {
        const r2 = await fetch(`${this.apiBase}/yjs/state?lastId=${this.lastId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r2.ok) break;
        const d2 = await r2.json();
        for (const u of d2.updates) {
          const bytes = b64(u.update);
          Y.applyUpdate(this.doc, bytes, 'remote');
          Y.applyUpdate(serverDoc, bytes);
          this.lastId = Math.max(this.lastId, u.id);
        }
        hasMore = !!d2.hasMore;
      }

      // Conflict detection: if applying server state caused node count to drop
      // drastically (>= 50% while previously >= 5 nodes), it likely means we just
      // applied destructive operations from another (stale) client. Take a
      // pre-sync-conflict snapshot of the pre-state and emit a warning.
      const postNodeCount = this._safeNodeCount(this.doc);
      if (preNodeCount >= 5 && postNodeCount < preNodeCount * 0.5 && preState) {
        console.warn('[Sync] Drastic node-count drop detected: ' + preNodeCount + ' -> ' + postNodeCount);
        this._sendSnapshot(preState, 'pre-sync-conflict',
          `dropped from ${preNodeCount} to ${postNodeCount} nodes on sync`).catch(() => {});
        this.emit('data-loss-warning', [{ before: preNodeCount, after: postNodeCount }]);
      }

      this.synced = true;
      this.emit('synced', []);

      // 3. Push local-only edits to server
      const sv = Y.encodeStateVector(serverDoc);
      serverDoc.destroy();
      const diff = Y.encodeStateAsUpdate(this.doc, sv);
      if (diff.byteLength > 2) {
        console.log('[Sync] Pushing local-only edits (' + diff.byteLength + 'B)');
        await this._pushToDb(diff);
      }

      // 4. Connect Broadcast channel (fast path)
      await this._connectBroadcast();

      // 5. Fallback polling (slow path, in case Broadcast misses)
      this._startPolling();

      // 6. Sync on tab focus / iOS bfcache resume / window focus
      document.removeEventListener('visibilitychange', this._onVisibility);
      document.addEventListener('visibilitychange', this._onVisibility);
      window.removeEventListener('pageshow', this._onPageShow);
      window.addEventListener('pageshow', this._onPageShow);
      window.removeEventListener('focus', this._onFocus);
      window.addEventListener('focus', this._onFocus);

      this.connected = true;
      this.emit('status', [{ status: 'connected' }]);
      console.log('[Sync] Connected. lastId =', this.lastId);

      // Trigger immediate auto-daily snapshot on connect (covers the case where the user
      // hasn't been online at the previous JST 00:00). Tier timers handle ongoing capture.
      this.takeAutoDailySnapshot().catch(() => {});
      this._startSnapshotTimers();
    } catch (e) {
      console.error('[Sync] Connect error:', e);
      this.emit('status', [{ status: 'error', error: e }]);
      if (!this.destroyed) setTimeout(() => this.connect(), 5000);
    }
  }

  async _onResume() {
    if (this.destroyed) return;
    // Debounce: multiple events (visibility + focus + pageshow) may fire together.
    // 500ms is enough to coalesce duplicates without slowing genuine tab switches.
    const now = Date.now();
    if (now - this._lastResumeAt < 500) return;
    this._lastResumeAt = now;

    // If connection was lost (e.g. mobile suspend), full reconnect
    if (!this.connected) {
      this.connect();
      return;
    }
    // Reconnect Broadcast only if it dropped (broadcastReady flag false).
    if (!this.broadcastReady) await this._connectBroadcast();
    // 2. Push local changes first, then pull remote (sequential)
    await this._flushBatch();
    await this._fetchNew();
    this._startPolling();
    // 3. Force a UI refresh — observers may have fired with a throttled timer
    //    while we were hidden, leaving stale DOM. Emit 'synced' so main.js
    //    re-renders the tree explicitly.
    this.emit('synced', []);
  }

  _onVisibility() {
    if (this.destroyed) return;
    if (document.visibilityState === 'visible') {
      this._onResume();
      this._startSnapshotTimers();
    } else {
      this._stopPolling();
      // ⚠️ 重要: hidden になる瞬間に即 flush。background tab の setTimeout は
      // 1分間隔に throttle されるため、batchTimer (1s) を待つと反映が
      // 大幅に遅れて「別タブで開いたら見えない」問題の原因になる。
      this._flushBatch().catch(() => {});
      // Capture one final snapshot reflecting work done while visible, then stop timers.
      this._takeTierSnapshot('auto-3min').catch(() => {});
      this._stopSnapshotTimers();
    }
  }

  _onPageShow(e) {
    // iOS bfcache: page restored from cache, need full resync
    if (e.persisted) this._onResume();
  }

  _onFocus() {
    this._onResume();
  }

  async disconnect() {
    this.doc.off('update', this._onDocUpdate);
    document.removeEventListener('visibilitychange', this._onVisibility);
    window.removeEventListener('pageshow', this._onPageShow);
    window.removeEventListener('focus', this._onFocus);
    await this._flushBatch();
    this._stopPolling();
    this._stopSnapshotTimers();
    this._disconnectBroadcast();
    this.connected = false;
    this.emit('status', [{ status: 'disconnected' }]);
  }

  destroy() {
    this.destroyed = true;
    this.disconnect();
    super.destroy();
  }

  // ── Write: local edit → Broadcast (instant) + DB (durable) ──

  _onDocUpdate(update, origin) {
    if (origin === 'remote') return;
    this.pendingUpdates.push(update);

    // Broadcast immediately for real-time (no batching)
    this._broadcast(update);

    // DB write is batched for efficiency
    if (!this.batchTimer) {
      this.batchTimer = setTimeout(() => {
        this.batchTimer = null;
        this._flushBatch();
      }, BATCH_DB);
    }
  }

  // ── Broadcast: instant peer-to-peer via Supabase Realtime ──

  async _connectBroadcast() {
    this._disconnectBroadcast();

    if (!this.supabase) {
      const res = await fetch(`${this.apiBase}/config`);
      if (!res.ok) throw new Error('Config fetch failed');
      const config = await res.json();
      this.supabase = createClient(config.supabaseUrl, config.anonKey, {
        auth: { persistSession: false },
      });
    }

    const token = this.getToken();
    this.supabase.realtime.setAuth(token);

    this.channel = this.supabase.channel(`yjs-broadcast-${this.userId}`, {
      config: { broadcast: { self: false } },  // Don't echo back own messages
    });

    this.channel.on('broadcast', { event: 'yjs-update' }, (payload) => {
      try {
        const bytes = b64(payload.payload.update);
        Y.applyUpdate(this.doc, bytes, 'remote');
      } catch (e) {
        console.error('[Sync] Broadcast apply error:', e);
      }
    });

    // Peer selection: receive which memo the other PC has open
    this.channel.on('broadcast', { event: 'selection' }, (payload) => {
      this.emit('presence', [[{ selectedId: payload.payload.selectedId }]]);
    });

    this.broadcastReady = false;
    this.channel.subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        this.broadcastReady = true;
        this._broadcastReconnectAttempt = 0;  // reset backoff on success
        console.log('[Sync] Broadcast connected');
        if (this.pollTimer) this._startPolling();
      } else if (status === 'CLOSED' || status === 'CHANNEL_ERROR') {
        this.broadcastReady = false;
        console.warn('[Sync] Broadcast disconnected:', status);
        if (this.pollTimer) this._startPolling();
        if (!this.destroyed && this.connected) {
          // Exponential backoff with jitter: 3s, 6s, 12s, 24s, 48s, ..., capped at 120s
          if (this._broadcastReconnectTimer) clearTimeout(this._broadcastReconnectTimer);
          const attempt = this._broadcastReconnectAttempt++;
          const base = Math.min(3000 * Math.pow(2, attempt), 120000);
          const jitter = Math.random() * 1000;
          this._broadcastReconnectTimer = setTimeout(() => this._connectBroadcast(), base + jitter);
        }
      }
    });
  }

  _broadcast(update) {
    if (!this.channel || !this.broadcastReady) return;
    try {
      this.channel.send({
        type: 'broadcast',
        event: 'yjs-update',
        payload: { update: toB64(update) },
      });
    } catch (e) {
      // Broadcast failed, DB write will handle durability
    }
  }

  trackSelection(memoId) {
    if (!this.channel || !this.broadcastReady) return;
    this.channel.send({
      type: 'broadcast',
      event: 'selection',
      payload: { selectedId: memoId },
    });
  }

  _disconnectBroadcast() {
    this.broadcastReady = false;
    if (this._broadcastReconnectTimer) {
      clearTimeout(this._broadcastReconnectTimer);
      this._broadcastReconnectTimer = null;
    }
    if (this.channel) {
      this.supabase?.removeChannel(this.channel);
      this.channel = null;
    }
  }

  // ── DB write (batched, for durability) ──

  async _pushToDb(update) {
    const token = this.getToken();
    if (!token) return;
    try {
      const res = await fetch(`${this.apiBase}/yjs/update`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: update,
      });
      if (res.ok) {
        const data = await res.json();
        this.lastId = Math.max(this.lastId, data.id);
      }
    } catch (e) {
      console.error('[Sync] DB push error:', e);
    }
  }

  async _flushBatch() {
    if (this.pendingUpdates.length === 0) return;
    const merged = Y.mergeUpdates(this.pendingUpdates);
    this.pendingUpdates = [];

    const token = this.getToken();
    if (!token) return;

    try {
      // Show syncing only if request takes >500ms (avoid flicker)
      const syncTimer = setTimeout(() => {
        this.emit('status', [{ status: 'syncing' }]);
      }, 500);
      const res = await fetch(`${this.apiBase}/yjs/update`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: merged,
      });
      clearTimeout(syncTimer);
      if (!res.ok) {
        if (res.status === 401) { this.emit('auth-error', []); return; }
        throw new Error(`Update failed: ${res.status}`);
      }
      const data = await res.json();
      this.lastId = Math.max(this.lastId, data.id);
      if (data.needsCompaction) this._compact();
      this.emit('status', [{ status: 'connected' }]);
    } catch (e) {
      clearTimeout(syncTimer);
      console.error('[Sync] Flush error:', e);
      this.pendingUpdates.unshift(merged); // restore for retry
      this.emit('status', [{ status: 'error', error: e }]);
    }
  }

  async _compact() {
    const token = this.getToken();
    if (!token) return;
    try {
      const state = Y.encodeStateAsUpdate(this.doc);
      await fetch(`${this.apiBase}/yjs/compact?maxId=${this.lastId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: state,
      });
    } catch (e) {
      console.error('[Sync] Compact error:', e);
    }
  }

  // ── Snapshots ─────────────────────────────────────────────────────────

  // Count alive nodes (used by conflict detection).
  _safeNodeCount(doc) {
    try { return doc.getMap('nodes').size; } catch { return 0; }
  }

  // Upload a Y.Doc state binary to the snapshot store.
  async _sendSnapshot(stateBytes, kind, note) {
    const token = this.getToken();
    if (!token) return null;
    const qs = new URLSearchParams({ kind });
    if (note) qs.set('note', note);
    try {
      const res = await fetch(`${this.apiBase}/yjs/snapshot?${qs.toString()}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: stateBytes,
      });
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      console.error('[Sync] Snapshot push error:', e);
      return null;
    }
  }

  // Compute a small fingerprint of the current Y.Doc to detect changes between snapshots.
  // Uses encodeStateVector (compact, deterministic) and hashes it.
  _docFingerprint() {
    try {
      const sv = Y.encodeStateVector(this.doc);
      // Cheap FNV-1a 32-bit hash over the bytes.
      let h = 0x811c9dc5;
      for (let i = 0; i < sv.length; i++) {
        h ^= sv[i];
        h = (h * 0x01000193) >>> 0;
      }
      return h;
    } catch { return 0; }
  }

  // Take a tier snapshot (auto-3min / auto-hourly / auto-daily). Skips if Y.Doc has
  // not changed since the last snapshot of any tier (state-vector fingerprint compare).
  async _takeTierSnapshot(kind) {
    if (!this.synced) return null;
    if (this._safeNodeCount(this.doc) === 0) return null;
    const fp = this._docFingerprint();
    if (fp === this._lastSnapshotFingerprint) return null; // no change → skip
    const state = Y.encodeStateAsUpdate(this.doc);
    const r = await this._sendSnapshot(state, kind, null);
    if (r?.ok) this._lastSnapshotFingerprint = fp;
    return r;
  }

  async takeAutoDailySnapshot() { return this._takeTierSnapshot('auto-daily'); }

  // Manual snapshot (user button) — always saved, never deduped.
  async takeManualSnapshot(note) {
    const state = Y.encodeStateAsUpdate(this.doc);
    return this._sendSnapshot(state, 'manual', note || null);
  }

  // ── Tier timers (3min / hourly / daily) ──
  //
  // 3-min and hourly fire on setInterval and only emit if the Y.Doc actually changed.
  // Daily is scheduled at the next JST 00:00 (rescheduled after firing).
  // visibilitychange pauses timers while the tab is hidden (no edits possible).
  _startSnapshotTimers() {
    this._stopSnapshotTimers();
    this._snap3minTimer  = setInterval(() => this._takeTierSnapshot('auto-3min'),  3  * 60 * 1000);
    this._snapHourlyTimer = setInterval(() => this._takeTierSnapshot('auto-hourly'), 60 * 60 * 1000);
    this._scheduleNextDaily();
  }
  _stopSnapshotTimers() {
    if (this._snap3minTimer)   { clearInterval(this._snap3minTimer);   this._snap3minTimer = null; }
    if (this._snapHourlyTimer) { clearInterval(this._snapHourlyTimer); this._snapHourlyTimer = null; }
    if (this._snapDailyTimer)  { clearTimeout(this._snapDailyTimer);   this._snapDailyTimer = null; }
  }
  _scheduleNextDaily() {
    if (this._snapDailyTimer) clearTimeout(this._snapDailyTimer);
    // Next JST 00:00. JST = UTC+9, so 15:00 UTC of (today or tomorrow).
    const now = new Date();
    const nowUtc = now.getTime();
    const jstNow = nowUtc + 9 * 3600 * 1000;
    const jstDay = Math.floor(jstNow / 86400000);
    const nextJstMidnightMs = (jstDay + 1) * 86400000 - 9 * 3600 * 1000;
    const delay = Math.max(60000, nextJstMidnightMs - nowUtc); // at least 1 minute
    this._snapDailyTimer = setTimeout(async () => {
      await this._takeTierSnapshot('auto-daily');
      this._scheduleNextDaily();
    }, delay);
  }

  // ── Fallback polling ──

  _startPolling() {
    this._stopPolling();
    const interval = this.broadcastReady ? POLL_SLOW : POLL_FAST;
    this.pollTimer = setInterval(() => this._fetchNew(), interval);
  }

  _stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  async _fetchNew() {
    if (this.destroyed) return;
    const token = this.getToken();
    if (!token) return;
    try {
      let hasMore = true;
      let safety = 50;
      while (hasMore && safety-- > 0 && !this.destroyed) {
        const res = await fetch(`${this.apiBase}/yjs/state?lastId=${this.lastId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data.updates.length > 0) {
          console.log('[Sync] Poll: got', data.updates.length, 'updates');
          for (const u of data.updates) {
            Y.applyUpdate(this.doc, b64(u.update), 'remote');
            this.lastId = Math.max(this.lastId, u.id);
          }
        }
        hasMore = !!data.hasMore;
      }
    } catch (e) {
      console.error('[Sync] Poll error:', e);
    }
  }
}

// ── Helpers ──

function b64(s) {
  const bin = atob(s);
  const a = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) a[i] = bin.charCodeAt(i);
  return a;
}

function toB64(bytes) {
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let s = '';
  for (let i = 0; i < u8.length; i++) s += String.fromCharCode(u8[i]);
  return btoa(s);
}
